import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Car, Bike, Wrench, Clock, Gauge, ChevronDown, Lightbulb, Loader2 } from "lucide-react";

const DIFF_COLOR = {
  Facile: "#10B981",
  Media: "#F59E0B",
  Difficile: "#EF4444",
};

export default function Guides() {
  const [filter, setFilter] = useState("tutti");
  const [guides, setGuides] = useState(null);
  const [open, setOpen] = useState(null);
  const [params] = useSearchParams();
  const cardRefs = useRef({});

  useEffect(() => {
    api.get("/guides").then((res) => setGuides(res.data));
  }, []);

  useEffect(() => {
    const target = params.get("open");
    if (target && guides) {
      const g = guides.find((x) => x.id === target);
      if (g) {
        setFilter("tutti");
        setOpen(target);
        setTimeout(() => {
          cardRefs.current[target]?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 250);
      }
    }
  }, [params, guides]);

  if (guides === null)
    return (
      <div className="pt-24 min-h-screen flex justify-center">
        <Loader2 className="w-8 h-8 text-[#F97316] animate-spin mt-16" />
      </div>
    );

  const filtered = filter === "tutti" ? guides : guides.filter((g) => g.vehicle === filter);

  return (
    <div className="pt-24 pb-20 max-w-4xl mx-auto px-5">
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-2">Guide alle Riparazioni</h1>
      <p className="text-zinc-400 mb-8">Istruzioni pratiche passo-passo per i guasti piu comuni di auto e moto.</p>

      <div className="flex gap-2 mb-8">
        {[
          { id: "tutti", label: "Tutti", icon: Wrench },
          { id: "auto", label: "Auto", icon: Car },
          { id: "moto", label: "Moto", icon: Bike },
        ].map((f) => (
          <button
            key={f.id}
            data-testid={`guide-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
              filter === f.id ? "border-[#F97316] bg-[#F97316]/10 text-[#F97316]" : "border-white/10 text-zinc-400 hover:border-white/30"
            }`}
          >
            <f.icon className="w-4 h-4" /> {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {filtered.map((g) => {
          const isOpen = open === g.id;
          const VIcon = g.vehicle === "moto" ? Bike : Car;
          return (
            <div key={g.id} ref={(el) => (cardRefs.current[g.id] = el)} data-testid={`guide-card-${g.id}`} className="rounded-2xl bg-[#111823] border border-white/8 overflow-hidden">
              <button onClick={() => setOpen(isOpen ? null : g.id)} className={`w-full text-left p-5 hover:bg-white/[0.02] transition-colors ${isOpen ? "bg-white/[0.02]" : ""}`}>
                <div className="flex items-start gap-4">
                  <span className="w-11 h-11 rounded-xl bg-[#F97316]/10 flex items-center justify-center shrink-0">
                    <VIcon className="w-5 h-5 text-[#F97316]" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs uppercase tracking-wider text-[#06B6D4] font-semibold">{g.category}</span>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ color: DIFF_COLOR[g.difficulty], backgroundColor: DIFF_COLOR[g.difficulty] + "22" }}>{g.difficulty}</span>
                    </div>
                    <h3 className="font-display font-bold text-lg leading-tight">{g.title}</h3>
                    <p className="text-sm text-zinc-400 mt-1">{g.summary}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {g.time}</span>
                      <span className="flex items-center gap-1"><Wrench className="w-3.5 h-3.5" /> {g.tools.length} strumenti</span>
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform shrink-0 ${isOpen ? "rotate-180" : ""}`} />
                </div>
              </button>
              {isOpen && (
                <div className="px-5 pb-6 border-t border-white/5 rise">
                  <div className="py-4">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 mb-2"><Wrench className="w-4 h-4" /> Strumenti necessari</div>
                    <div className="flex flex-wrap gap-2">
                      {g.tools.map((t, i) => (
                        <span key={i} className="text-xs font-mono px-2 py-1 rounded bg-white/5 text-zinc-200">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-3">
                    {g.steps.map((s, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="w-7 h-7 shrink-0 rounded-lg bg-[#F97316]/15 text-[#F97316] font-mono font-bold text-sm flex items-center justify-center">{i + 1}</div>
                        <div className="flex-1 rounded-lg bg-[#1A2232] border border-white/5 p-3">
                          <div className="font-semibold text-sm text-zinc-100 mb-1">{s.title}</div>
                          <p className="text-sm text-zinc-400 leading-relaxed">{s.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  {g.tips?.length > 0 && (
                    <div className="mt-4 rounded-lg bg-[#06B6D4]/10 border border-[#06B6D4]/30 p-3">
                      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#06B6D4] mb-2"><Lightbulb className="w-4 h-4" /> Consigli</div>
                      <ul className="space-y-1">
                        {g.tips.map((t, i) => (
                          <li key={i} className="text-sm text-cyan-100 flex gap-2"><span>•</span> {t}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
